import InnerContainer from "@/components/ContentContainer/InnerContainer";
import useAnalytics from "@/hooks/useAnalytics";
import useTranslation from "@/hooks/useTranslation";
import { Col, message, Row } from "antd";
import { useEffect, useState } from "react";
import {
  PackagingMaterialMatchOption,
  useBulkPatchPackagingMaterial,
  useGetPackagingMaterial,
  useGetPackagingMaterialOptions,
} from "../usePackagingMaterials";
import styles from "./PackagingMaterialDetail.module.scss";
import MaterialMatchCascader from "@/pages/packaging_materials/PackagingMaterialDetail/MaterialMatchCascader";
import Skeleton from "@/components/Skeleton/Skeleton";
import StatusTag from "@/components/StatusTag/StatusTag";
import ImpactPill from "@/components/Impact/ImpactPill";

export const getOption = (
  dataset_id: number,
  options?: PackagingMaterialMatchOption[]
): PackagingMaterialMatchOption | undefined => {
  if (!options) {
    return;
  }
  for (const option of options) {
    if (option.dataset_id === dataset_id) {
      return option;
    }
    if (option.children) {
      const opt = getOption(dataset_id, option.children);
      if (opt) {
        return opt;
      }
    }
  }
};

export default function PackagingMaterialImpact({ materialId }: { materialId: number }) {
  const { data, isLoading, isFetching: dataIsFetching } = useGetPackagingMaterial({ materialId });
  const [selectedOption, setSelectedOption] = useState<PackagingMaterialMatchOption | undefined>(
    undefined
  );
  const bulkPatch = useBulkPatchPackagingMaterial();
  const { data: options, isLoading: optionsIsLoading } = useGetPackagingMaterialOptions();

  const analytics = useAnalytics();
  const translation = useTranslation();

  useEffect(() => {
    if (data?.match?.id) {
      setSelectedOption(getOption(data.match.id, options));
    } else {
      setSelectedOption(undefined);
    }
  }, [data]);

  const onSubmit = (_: any, selectedOptions: PackagingMaterialMatchOption[] | undefined) => {
    if (selectedOptions) {
      setSelectedOption(selectedOptions[selectedOptions.length - 1]);
      return bulkPatch
        .mutateAsync({
          materialIds: [materialId],
          patch: {
            impact_dataset_id: selectedOptions[selectedOptions.length - 1].dataset_id,
          },
        })
        .then(() => {
          analytics.track(`Packaging Material Reference Updated`);
          message.success("Impact reference updated");
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    } else {
      setSelectedOption(undefined);
      bulkPatch
        .mutateAsync({
          materialIds: [materialId],
          patch: {
            clear: ["impact_dataset_id"],
          },
        })
        .then(() => {
          message.success(`Impact reference deleted.`);
          analytics.track("Impact Reference Deleted");
        })
        .catch(() => {
          message.error(translation.errors.general);
        });
    }
  };

  return (
    <InnerContainer
      title="Impact reference"
      contentClassName={styles.MaterialDetails}
    >
      <Row
        className={styles.leftContainer}
        gutter={[8, 8]}
      >
        <Col span={24}>
          <Row
            className={styles.impactContainer}
            style={{
              border: data?.status === "incomplete" ? "1px solid #FAAD14" : "1px solid #F0F0F0",
            }}
            align={"middle"}
          >
            <Col span={16}>
              {isLoading || optionsIsLoading ? (
                <Skeleton style={{ height: "30px", width: "100%" }} />
              ) : (
                <MaterialMatchCascader
                  dataset_id={data?.match?.id}
                  onChange={onSubmit}
                  loading={dataIsFetching || bulkPatch.isLoading}
                />
              )}
            </Col>
            <Col
              span={8}
              className={styles.impactStatus}
            >
              {isLoading || dataIsFetching || bulkPatch.isLoading ? (
                <Skeleton style={{ height: "26px", width: "92px", alignSelf: "end" }} />
              ) : data?.status === "incomplete" ? (
                <StatusTag status={"incomplete"} />
              ) : (
                <StatusTag status={"complete"} />
              )}
            </Col>
            <Col
              span={24}
              className={styles.impactRow}
            >
              <ImpactPill
                impact={selectedOption?.impact}
                type="borderless"
                loading={isLoading || dataIsFetching}
                before={
                  selectedOption && (
                    <span className={styles.impactUnit}>1 {selectedOption.unit}</span>
                  )
                }
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </InnerContainer>
  );
}
